import { computed, type MaybeRefOrGetter, type Ref } from 'vue';
import { useMutation, useQuery } from '@tanstack/vue-query';
import http from '@/services/http';
import { toast } from 'vue-sonner';
import type { ModelPermissions, PermissionCategory, PermissionRow, PermissionUser } from '@/types/types';

export function usePermissionUsers(enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery<PermissionUser[]>({
    queryKey: ['permission-users'],
    queryFn: () =>
      http.get<PermissionUser[]>('/api/permissions/users').then((response) => response.data),
    enabled,
  });
}

export function usePermissionModels() {
  return useQuery<PermissionCategory[]>({
    queryKey: ['permission-models'],
    queryFn: () =>
      http.get<PermissionCategory[]>('/api/permissions/models').then((response) => response.data),
  });
}

export function useUserPermissions(userId: Ref<number | null>) {
  return useQuery<Record<string, ModelPermissions>>({
    queryKey: ['user-permissions', userId],
    queryFn: () =>
      http
        .get<Record<string, ModelPermissions>>(`/api/permissions/${userId.value}`)
        .then((response) => response.data),
    enabled: computed(() => userId.value !== null),
  });
}

export function useSavePermissions() {
  return useMutation({
    mutationFn: ({ userId, permissions }: { userId: number; permissions: PermissionRow[] }) =>
      http.put(`/api/permissions/${userId}`, { permissions }),
    onSuccess: () => {
      toast.message('Permissions Saved', {
        id: 'permissions-save',
        duration: 2000,
        important: false,
      });
    },
    onError: () => {
      toast.error('Failed to save permissions.', { duration: 5000 });
    },
  });
}
